import { useEffect, useState } from "react";
import { Search, Copy, Trash2, History as HistoryIcon, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import AdSlot from "@/components/AdSlot";

export default function History() {
  const [items, setItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [aiTool, setAiTool] = useState("");
  const [audience, setAudience] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (keyword) params.set("keyword", keyword);
    if (aiTool) params.set("ai_tool", aiTool);
    if (audience) params.set("audience", audience);
    const { data } = await api.get(`/prompts/history?${params}`);
    setItems(data.items || []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const del = async (id) => {
    await api.delete(`/prompts/${id}`);
    setItems((it) => it.filter((p) => p.id !== id));
    toast.success("Deleted");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6] mb-2">History</div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">All your prompts.</h1>
      </div>

      <AdSlot variant="banner" className="mb-6" />

      {/* Filters */}
      <div className="grid md:grid-cols-4 gap-3 p-4 bg-white border border-[#E2E8F0] rounded-md mb-6">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input data-testid="filter-keyword" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Search keyword / ask…" className="pl-9" />
        </div>
        <Input data-testid="filter-ai-tool" value={aiTool} onChange={(e) => setAiTool(e.target.value)} placeholder="AI tool" />
        <div className="flex gap-2">
          <Input data-testid="filter-audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Audience" />
          <Button onClick={load} className="bg-[#1F3A8A] hover:bg-[#1E40AF] text-white" data-testid="apply-filters-btn">Filter</Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#F5F7FA] text-xs uppercase tracking-wider text-[#64748B]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-left px-4 py-3 font-semibold">AI Tool</th>
              <th className="text-left px-4 py-3 font-semibold">Ask</th>
              <th className="text-left px-4 py-3 font-semibold">Audience</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-16 text-center text-[#64748B]">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-16 text-center text-[#64748B]">
                <HistoryIcon className="w-10 h-10 mx-auto mb-3 text-[#1F3A8A]/30" />
                No prompts found.
              </td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="hover:bg-[#F5F7FA] cursor-pointer" onClick={() => setSelected(p)} data-testid={`history-row-${p.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-[#64748B] whitespace-nowrap">{new Date(p.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 text-xs bg-[#1F3A8A]/10 text-[#1F3A8A] rounded font-semibold">{p.inputs.ai_tool}</span>
                </td>
                <td className="px-4 py-3 text-[#111827] truncate max-w-md">{p.inputs.ask}</td>
                <td className="px-4 py-3 text-[#64748B]">{p.inputs.audience}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => copy(p.output)} className="p-1.5 text-[#64748B] hover:text-[#1F3A8A]" data-testid={`copy-${p.id}`}>
                      <Copy className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(p.id)} className="p-1.5 text-[#64748B] hover:text-[#EF4444]" data-testid={`del-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto w-[calc(100vw-2rem)] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-lg sm:text-2xl pr-6">{selected?.inputs.ai_tool} · {new Date(selected?.created_at).toLocaleDateString()}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#14B8A6] mb-2">Inputs</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {Object.entries(selected.inputs).map(([k, v]) => (
                    <div key={k} className="p-3 bg-[#F5F7FA] border border-[#E2E8F0] rounded">
                      <div className="text-xs font-semibold text-[#64748B] uppercase">{k.replace("_", " ")}</div>
                      <div className="text-[#111827] mt-1">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#14B8A6]">Output</div>
                  <div className="flex items-center gap-2">
                    {selected.model && (
                      <div className="hidden sm:flex items-center gap-1 px-2 py-0.5 rounded bg-[#14B8A6]/10 border border-[#14B8A6]/20 text-[10px] font-semibold text-[#0D9488] uppercase tracking-wider">
                        <Sparkles className="w-3 h-3" /> {selected.model.includes("gemini") ? "Gemini Flash" : selected.model}
                      </div>
                    )}
                    <Button size="sm" onClick={() => copy(selected.output)} className="bg-[#1F3A8A] hover:bg-[#1E40AF] gap-2" data-testid="dialog-copy">
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </Button>
                  </div>
                </div>
                <pre className="whitespace-pre-wrap font-mono text-sm bg-[#F5F7FA] border border-[#E2E8F0] rounded p-4">{selected.output}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
